import { STEPS, type StepId } from '@engpod/core';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { cached, readSnapshot } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from './Icon';

export interface DockSteps {
  done: Partial<Record<StepId, boolean>>;
  open: StepId | null;
  onStep(id: StepId): void;
}

/** Bottom bar. On the learn page it also carries this episode's five steps. */
export function Dock({ steps }: { steps?: DockSteps }) {
  const { me } = useAuth();
  const location = useLocation();
  const [due, setDue] = useState(0);
  const footer = useRef<HTMLElement>(null);
  // Publish the dock height so the step panel can sit right above it.
  useEffect(() => {
    const el = footer.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--dock-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (!me?.user) return setDue(0);
    const refresh = () => void cached<{ due: number }>('/review/stats').fresh.then(({ data }) => setDue(data.due), () => undefined);
    setDue(readSnapshot<{ due: number }>('/review/stats')?.due ?? 0);
    refresh();
    // Pages that change what is due (review, saving words) announce it.
    window.addEventListener('engpod:review-changed', refresh);
    return () => window.removeEventListener('engpod:review-changed', refresh);
  }, [me?.user, location.pathname, steps?.open]);
  const doneCount = steps ? STEPS.filter((s) => steps.done[s.id]).length : 0;
  const nextStep = steps ? (STEPS.find((s) => !steps.done[s.id])?.id ?? 'output') : 'gist';

  return (
    <footer ref={footer} className={`home-footer${steps ? '' : ' nav-only'}`}>
      {steps && (
        <>
          <div className="daily-ribbon">
            <button onClick={() => steps.onStep(nextStep)}>
              <Icon name="headphones" /> 本集学习 <span>{doneCount}/5</span>
            </button>
            <NavLink to="/vocab">我的生词本</NavLink>
          </div>
          <div className="daily-pills">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                className={`${steps.done[s.id] ? 'done' : ''}${steps.open === s.id ? ' open' : ''}`}
                onClick={() => steps.onStep(s.id)}
                aria-pressed={steps.open === s.id}
              >
                <span>{s.label}</span>
                <b>{steps.done[s.id] ? '✓' : String(i + 1).padStart(2, '0')}</b>
              </button>
            ))}
          </div>
        </>
      )}
      <nav aria-label="主菜单">
        <NavLink to="/" end>
          <Icon name="headphones" />
          <span>精听</span>
        </NavLink>
        <NavLink to="/vocab">
          <Icon name="bookmark" />
          <span>生词本</span>
        </NavLink>
        <NavLink to="/review">
          <Icon name="clipboard" />
          <span>复习{due > 0 ? ` · ${due > 99 ? '99+' : due}` : ''}</span>
        </NavLink>
        <NavLink to="/library">
          <Icon name="library" />
          <span>资料库</span>
        </NavLink>
        <NavLink to="/me">
          <Icon name="user" />
          <span>我的</span>
        </NavLink>
      </nav>
    </footer>
  );
}
